import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  ARIA_OPENING_CUE,
  AriaInteractionMode,
  buildAriaSpeakAloudCue,
  isSpokenAriaMode
} from '../../../../../../pure-js/types/AppAssistant'
import { CoachConversationMode, CoachMessageRole, type GoalScope } from '../../../../../../pure-js/types/CoachTypes'
import type { CoachVoiceTurn } from '../../../../../../pure-js/types/CoachApi'
import { isCallableError, toError } from '../../../../../../pure-js/libs/Common'
import { meaningfulAriaTranscriptTurns, toCoachVoiceTurns } from '../../../../../../pure-js/libs/ariaTranscript'
import { sendErrorToSlack } from '../../../../libs/SlackHelper'
import {
  useAppendCoachVoiceTurns,
  useCoachConversations,
  useCoachStatus,
  useEndCoachConversation
} from '../../../../apiLayer/queryHooks/coach'
import { QUERY_KEYS } from '../../../../apiLayer/queryKeys'
import { useAriaInteractionMode } from '../../../../shared/hooks/useAriaInteractionMode'
import { isVoiceConnecting } from './coachVoiceStatus'
import { waitForAriaToFinishSpeaking } from '../../../../shared/components/AppAssistant/realtime/assistantAudioPipeline'
import { findMostRecentOpenConversation, findResumableConversationId } from './coachTranscript'
import { flowOfFocus, isConversationOfFocus, isFirstGoalSettingFor } from './coachFocusHelpers'
import { useCoachChat, type UseCoachChatResult } from './useCoachChat'
import { useCoachVoiceSession } from './useCoachVoiceSession'
import { useAppSelector } from '../../../../store'
import { isPlatformTourOnScreen } from '../../platformTour/platformTourStops'

export type UseCoachInteractionParams = {
  /** Which conversation this is: the person's own development, or how they lead. */
  focus: GoalScope
}

export type UseCoachInteractionResult = {
  chat: UseCoachChatResult
  session: ReturnType<typeof useCoachVoiceSession>['session']
  mode: AriaInteractionMode
  isVoiceActive: boolean
  setMode: (mode: AriaInteractionMode) => void
  /** A conversation just produced the user's first goals; onboarding has more steps after this. */
  hasJustCompletedGoals: boolean
  /** This participant has yet to set their first goals. Unknown counts as yes — see the hook. */
  isFirstGoalSetting: boolean
  /** Open the conversation on request, resuming an unfinished one rather than adding another. */
  openConversation: () => void
  /** Move the page to another focus: a session running in the thread on screen is paused and saved, and the thread left. */
  leaveForFocus: (nextFocus: GoalScope) => void
  /** Send the draft, opening the conversation if this is the first thing said in it. */
  sendTypedMessage: () => void
  startVoice: () => void
  stopVoice: () => void
  restartVoiceForLanguage: () => void
}

/** Consecutive rejections before the transcript stops being pushed; the tail still rides along on stop. */
const MAX_CONSECUTIVE_FLUSH_FAILURES = 3

/** How the conversation is held — mode, live session, switching — so the page is only layout. */
export function useCoachInteraction({ focus }: UseCoachInteractionParams): UseCoachInteractionResult {
  // Read through a ref: the callbacks below are captured once and outlive the render that built them.
  const focusRef = useRef(focus)
  focusRef.current = focus

  // Declared before the chat because the chat needs to know not to adopt a mid-session refetch.
  const isVoiceActiveRef = useRef(false)
  const chat = useCoachChat({ isVoiceActiveRef })
  const queryClient = useQueryClient()
  const appendMutation = useAppendCoachVoiceTurns()
  const endMutation = useEndCoachConversation()
  const { data: conversationsData } = useCoachConversations()
  const { data: coachStatus } = useCoachStatus()
  const { mode, setMode, canAutoStartVoice } = useAriaInteractionMode({ remember: false })

  // A Live session is always spoken: the live model answers in audio only, so typing goes to
  // `sendCoachMessage` instead and no session is opened for it.
  const isSpokenMode = isSpokenAriaMode(mode)
  // Read by callbacks that outlive the render they were built in (the opening cue, the typed start).
  const isSpokenModeRef = useRef(isSpokenMode)
  isSpokenModeRef.current = isSpokenMode

  // Read through refs so the callbacks below keep a stable identity across transcript updates.
  const conversationRef = useRef(chat.selectedConversation)
  conversationRef.current = chat.selectedConversation

  const isFirstGoalSetting = isFirstGoalSettingFor(coachStatus)

  // Pick up the talk that is still open. Goal-setting follows the server, which resumes that one thread
  // whatever its age; everything else honours the grace window. Neither rule applies until status loads.
  // Only threads of the focus on screen: a leadership talk and a personal one never continue each other.
  const conversationsOfFocus = (conversationsData?.conversations ?? []).filter((conversation) =>
    isConversationOfFocus(conversation, focus)
  )

  const resolveConversationToOpen = (): string | undefined => {
    if (!coachStatus) return undefined
    if (isFirstGoalSetting) return findMostRecentOpenConversation(conversationsOfFocus)?.id
    return findResumableConversationId(conversationsOfFocus)
  }
  const openConversationId = resolveConversationToOpen()
  const selectConversation = chat.selectConversation
  const hasSelection = Boolean(chat.selectedId)
  useEffect(() => {
    if (hasSelection || !openConversationId) return
    selectConversation(openConversationId)
  }, [hasSelection, openConversationId, selectConversation])

  /*
   * A conversation with nothing in it is a dead end, so ask the server to open it.
   *
   * Aria's opening line is written when a conversation is started, and `ensureConversationId` skips
   * that call as soon as one is selected — so a thread left empty by a spoken session that never
   * connected was picked up, rendered as an empty chat, and never spoken into again. Asking is safe
   * whatever the state: the server greets only a thread that really is empty, and otherwise just hands
   * back what is already there.
   */
  const openedSilentRef = useRef<string | undefined>(undefined)
  const selectedId = chat.selectedId
  const isSilentConversation = Boolean(chat.selectedConversation && chat.messages.length === 0)
  const startConversation = chat.startConversation
  useEffect(() => {
    if (!selectedId || !isSilentConversation || isSpokenAriaMode(mode)) return
    if (openedSilentRef.current === selectedId) return
    openedSilentRef.current = selectedId
    void startConversation(CoachConversationMode.TEXT, { resume: true, flow: flowOfFocus(focusRef.current) })
  }, [isSilentConversation, mode, selectedId, startConversation])

  /**
   * The conversation this talk belongs to, created only if there is not one already.
   *
   * Two guards, because one was not enough. The promise ref collapses concurrent callers in this
   * component — a voice start racing a first typed message opened one thread each, ten milliseconds
   * apart. `resume: true` closes the rest: the server hands back the most recent unfinished
   * conversation instead of creating another, so a remount, a second tab or a retry all converge on
   * the same thread rather than each leaving an empty one behind.
   */
  const pendingConversationRef = useRef<Promise<string> | undefined>(undefined)
  const ensureConversationId = useCallback(
    async (mode: CoachConversationMode) => {
      // A concluded conversation cannot take new turns, so talking again opens a fresh one rather than
      // silently discarding everything said into the old thread.
      const existing = conversationRef.current
      const isUsableThread = existing && !existing.endedAt && isConversationOfFocus(existing, focusRef.current)
      if (isUsableThread) return existing.id

      pendingConversationRef.current ??= startConversation(mode, { resume: true, flow: flowOfFocus(focusRef.current) })
        .then((created) => {
          if (!created) throw new Error('coach-conversation-unavailable')
          return created
        })
        .finally(() => {
          pendingConversationRef.current = undefined
        })
      return pendingConversationRef.current
    },
    [startConversation]
  )

  const ensureVoiceConversationId = useCallback(
    () => ensureConversationId(CoachConversationMode.VOICE),
    [ensureConversationId]
  )

  // Indirection through a ref: the idle and navigation handlers are defined below, because both need
  // the session this call creates.
  const handleIdleTimeoutRef = useRef<() => void>(() => undefined)
  const handleIdleTimeout = useCallback(() => handleIdleTimeoutRef.current(), [])
  const handleConversationCompleteRef = useRef<() => void>(() => undefined)
  const handleConversationComplete = useCallback(() => handleConversationCompleteRef.current(), [])

  // Two cases: a line of hers that was read but never heard gets spoken aloud, otherwise she opens
  // normally. What she is walking into is already in the system instruction — the cue only starts her.
  const messagesRef = useRef(chat.messages)
  messagesRef.current = chat.messages
  const resolveOpeningCue = useCallback(() => {
    // A typed session is opened by a message that has already been sent and answered, so there is
    // nothing to open — and reading her last reply back would repeat it. She speaks when spoken to.
    if (!isSpokenModeRef.current) return ''
    const messages = messagesRef.current
    const lastMessage = messages[messages.length - 1]
    const isUnheardFromAria = lastMessage?.role === CoachMessageRole.ASSISTANT && !lastMessage.fromVoice
    return isUnheardFromAria ? buildAriaSpeakAloudCue(lastMessage.content) : ARIA_OPENING_CUE
  }, [])

  const ensureTypedConversationId = useCallback(
    () => ensureConversationId(CoachConversationMode.TEXT),
    [ensureConversationId]
  )

  const { session } = useCoachVoiceSession({
    ensureConversationId: isSpokenMode ? ensureVoiceConversationId : ensureTypedConversationId,
    onIdleTimeout: handleIdleTimeout,
    resolveOpeningCue,
    onConversationComplete: handleConversationComplete
  })

  // Only a SPOKEN session counts as "voice active" — it is what suppresses the microphone-less parts of
  // the UI and blocks the chat from adopting a mid-session refetch. A typed session is a chat.
  const isSessionLive = session.isLive || isVoiceConnecting(session.status)
  const isVoiceActive = isSpokenMode && isSessionLive
  // The refetch guard applies to BOTH: a typed session appends its turns server-side as they happen too,
  // so adopting the server copy mid-conversation would render every one of them twice.
  isVoiceActiveRef.current = isSessionLive

  // How many spoken turns are already saved. Only advanced on success, so a failed flush is retried
  // rather than leaving a hole in the transcript.
  const savedTurnCountRef = useRef(0)
  const isFlushingRef = useRef(false)
  const failedFlushCountRef = useRef(0)
  // Held by ref: the mutation object is a new identity on every render, so depending on it re-ran the
  // flush effect on every render — which turned one unfixable rejection into a request loop.
  const appendRef = useRef(appendMutation)
  appendRef.current = appendMutation
  // Each spoken session restarts the transcript from zero; a flush that lands after the restart must
  // not advance the new session's counter.
  const voiceRunIdRef = useRef(0)
  // Read from the session, not a render-time snapshot: stopping releases the captions still waiting on
  // Aria's audio, and saving runs in that same tick.
  const getTurns = session.getTurns
  // Held by ref so `flushTurns` keeps one identity: it is a dependency of the effect that runs on every
  // transcript fragment, and rebuilding it there would restart that effect on each one.

  // `consumed` counts the transcript, `turns` counts what is worth sending. Advancing the saved counter
  // by the sent count instead re-sends an already-saved turn every time one is dropped as noise.
  const takeUnsavedTurns = useCallback(
    (includeSpeakingTurn: boolean): { turns: CoachVoiceTurn[]; consumed: number } => {
      const spoken = getTurns()
      const ready = includeSpeakingTurn ? spoken : spoken.filter((turn) => turn.isComplete)
      const unsaved = ready.slice(savedTurnCountRef.current)
      return { turns: toCoachVoiceTurns(meaningfulAriaTranscriptTurns(unsaved)), consumed: unsaved.length }
    },
    [getTurns]
  )

  const flushTurns = useCallback(
    async (includeSpeakingTurn: boolean) => {
      const conversationId = conversationRef.current?.id
      if (!conversationId || isFlushingRef.current) return
      if (failedFlushCountRef.current >= MAX_CONSECUTIVE_FLUSH_FAILURES) return

      const pending = takeUnsavedTurns(includeSpeakingTurn)
      if (pending.turns.length === 0) return

      const runId = voiceRunIdRef.current
      isFlushingRef.current = true
      try {
        // Only a spoken session produces these turns now, which is what `fromVoice` records.
        await appendRef.current.mutateAsync({ conversationId, turns: pending.turns })
        if (runId === voiceRunIdRef.current) savedTurnCountRef.current += pending.consumed
        failedFlushCountRef.current = 0
      } catch (error) {
        // The words are still on screen and will be retried on the next turn or on stop. Counted,
        // because a rejection the retry cannot fix — an ended conversation, a transcript over the
        // limit — otherwise repeats as fast as the network allows for the rest of the session.
        failedFlushCountRef.current += 1
        // Only a fault of the page itself is reported; a failed call is retried by the next flush.
        if (!isCallableError(error)) void sendErrorToSlack(toError(error), undefined, 'useCoachInteraction.flushTurns')
      } finally {
        isFlushingRef.current = false
      }
    },
    [takeUnsavedTurns]
  )

  // Save as the conversation happens, not only at the end: it keeps the message count honest while the
  // user is still talking, survives a closed tab, and leaves something for typed chat to continue from.
  useEffect(() => {
    if (!session.isLive) return
    void flushTurns(false)
  }, [session.turns, session.isLive, flushTurns])

  // A typed session's turns ARE the conversation, so they stream into the chat; the tail is dropped as
  // soon as the session is not live, by which point it has been flushed and refetched.
  const setLiveTurns = chat.setLiveTurns
  const isTypedSessionLive = !isSpokenMode && session.isLive
  useEffect(() => {
    setLiveTurns(isTypedSessionLive ? session.turns : [])
  }, [isTypedSessionLive, session.turns, setLiveTurns])

  // Fires when the goals land, not when the talk ends — Aria saves them mid-conversation, and typed
  // chat has no Stop. Explicitly `=== false`, not `isFirstGoalSetting`: an unknown status on the first
  // render would record every returning participant as having arrived without goals.
  const arrivedWithoutGoalsRef = useRef(false)
  if (coachStatus?.onboardingGoalsCompleted === false) arrivedWithoutGoalsRef.current = true
  const goalsLandedInConversation = arrivedWithoutGoalsRef.current && coachStatus?.onboardingGoalsCompleted === true

  // The card belongs to the talk that produced the goals, and leaving that talk once retires it —
  // keyed on the conversation id alone it returned every time they re-opened that thread.
  const goalsCompletedConversationIdRef = useRef<string | undefined>(undefined)
  const hasLeftGoalsConversationRef = useRef(false)
  if (goalsLandedInConversation && !goalsCompletedConversationIdRef.current && chat.selectedId) {
    goalsCompletedConversationIdRef.current = chat.selectedId
  }
  const isOnGoalsConversation = Boolean(chat.selectedId) && goalsCompletedConversationIdRef.current === chat.selectedId
  if (goalsCompletedConversationIdRef.current && !isOnGoalsConversation) hasLeftGoalsConversationRef.current = true

  const hasJustCompletedGoals =
    goalsLandedInConversation && isOnGoalsConversation && !hasLeftGoalsConversationRef.current

  const startVoice = useCallback(() => {
    // The session refuses a second start of its own accord, but the counters below must not be reset
    // for a run that is already going — the next flush would re-send everything it had already saved.
    if (isVoiceActiveRef.current) return
    voiceRunIdRef.current += 1
    savedTurnCountRef.current = 0
    failedFlushCountRef.current = 0
    void session.start()
  }, [session])

  /** Persist everything said that is not saved yet, then let the typed view catch up. */
  const savePendingTurns = useCallback(async () => {
    // Read first: the page may have moved to another thread by the time the flush is done.
    const conversationId = conversationRef.current?.id
    await flushTurns(true)

    // Only now, not on every flush: the typed view is what needs the appended turns, and refetching
    // the whole thread after each spoken turn would double the traffic of a live conversation.
    if (conversationId) {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COACH_CONVERSATION_DETAIL(conversationId) })
    }
  }, [flushTurns, queryClient])

  /** Stop listening. Separate from saving, because switching to typing has to echo the turns between. */
  const stopListening = useCallback(() => {
    session.stop()
    // Stated here rather than waiting for the re-render, so restarting straight after a pause is not
    // racing React for the truth about whether a session is running.
    isVoiceActiveRef.current = false
  }, [session])

  /** Stop listening and save what was said. The conversation stays open. */
  const pauseVoice = useCallback(async () => {
    stopListening()
    await savePendingTurns()
  }, [savePendingTurns, stopListening])

  // A session that times out on silence stops listening but keeps the conversation open, so coming
  // back and pressing start continues where it left off rather than concluding it behind the user.
  handleIdleTimeoutRef.current = () => void pauseVoice()

  // Switching focus is a switch of conversation: the session is kept for when they come back, never cut off unsaved.
  const clearSelection = chat.clearSelection
  const leaveForFocus = useCallback(
    (nextFocus: GoalScope) => {
      const shownConversation = conversationRef.current
      const isShownConversationOfNextFocus = !!shownConversation && isConversationOfFocus(shownConversation, nextFocus)
      if (isShownConversationOfNextFocus) return

      if (isVoiceActiveRef.current) void pauseVoice()
      clearSelection()
    },
    [clearSelection, pauseVoice]
  )

  // Stop always asks the server to conclude. Only the server can tell whether goal-setting is finished —
  // it completes onboarding from the goals the person holds first, so someone who kept the three they
  // already had ends the talk, while someone who agreed nothing is refused and the thread stays open.
  const stopVoice = useCallback(() => {
    void (async () => {
      await pauseVoice()
      const conversationId = conversationRef.current?.id
      if (!conversationId || conversationRef.current?.endedAt) return
      try {
        // Anything a failed flush left behind rides along, so ending never drops the tail.
        await endMutation.mutateAsync({ conversationId, transcript: takeUnsavedTurns(true).turns })
      } catch (error) {
        // Only a fault of the page itself is reported; a refused or failed close leaves the talk open.
        if (!isCallableError(error)) void sendErrorToSlack(toError(error), undefined, 'useCoachInteraction.stopVoice')
      }
    })()
  }, [endMutation, pauseVoice, takeUnsavedTurns])

  // Aria said the talk is finished, so hang up rather than hold the microphone open on silence. The
  // close waits for playback to fall quiet, then goes through the same `stopVoice` that Stop does.
  const isHangingUpRef = useRef(false)
  handleConversationCompleteRef.current = () => {
    if (isHangingUpRef.current || !isVoiceActiveRef.current) return
    isHangingUpRef.current = true
    void waitForAriaToFinishSpeaking(session.getOutputLevel)
      .then(() => stopVoice())
      .finally(() => {
        isHangingUpRef.current = false
      })
  }

  /**
   * Start the conversation because the participant asked for it, and let Aria open it.
   *
   * The greeting is deliberately not suppressed here: this is the "Start a conversation" button, so
   * the first thing that should happen is Aria speaking — with the opening that fits the flow she is
   * in. Routed through `ensureConversationId` so pressing it twice, or pressing it while an unfinished
   * conversation is still loading, converges on one thread instead of opening a second.
   */
  const openConversation = useCallback(() => {
    // A start that failed leaves the empty state on screen, to be pressed again.
    void ensureConversationId(CoachConversationMode.TEXT).catch(() => undefined)
  }, [ensureConversationId])

  /**
   * Send a typed message, opening the conversation if this is the first one.
   *
   * Opening the page must never write. Creating a conversation on arrival meant a page visit cost a
   * Firestore document and an LLM greeting, and any remount or race left an extra empty thread behind —
   * which is exactly what happened.
   */
  const sendTypedMessage = useCallback(async () => {
    if (!chat.draft.trim()) return
    try {
      // The live model answers in audio only and refuses a TEXT session outright (close 1007), so a
      // typed message goes to `sendCoachMessage`, which builds the same instruction from the same
      // prompts. `sendDraft` keeps the words on screen and flags the bubble if the callable fails.
      const conversationId = await ensureConversationId(CoachConversationMode.TEXT)
      await chat.sendDraft(conversationId)
    } catch {
      // Only a start that failed lands here: the draft is untouched, so the user can press send again.
    }
  }, [chat, ensureConversationId])

  /*
   * A spoken session resumes an open conversation, and only that — starting one creates the
   * conversation it is held in, so auto-starting with nothing to resume made merely opening this page
   * write a thread and have Aria talk at someone who came to read their goals.
   *
   * It waits for the conversation itself, not just the list: on the list alone the session started
   * while the detail was still loading, `ensureConversationId` saw nothing to continue, and it opened
   * a new thread anyway — the very thing the gate exists to prevent. Once per visit, because keying
   * this on "not currently live" would restart the session the instant the user pressed Stop.
   */
  const hasAutoStartedRef = useRef(false)
  const resumableConversation = chat.selectedConversation
  const canResumeSpokenConversation = Boolean(resumableConversation && !resumableConversation.endedAt)
  // The platform tour passes through this page; Aria waits until it is gone.
  const isTourOnScreen = useAppSelector((state) => isPlatformTourOnScreen(state.ui.platformTour))
  const canAutoStartNow =
    canResumeSpokenConversation && canAutoStartVoice && !isTourOnScreen && !hasAutoStartedRef.current
  useEffect(() => {
    if (!canAutoStartNow) return
    hasAutoStartedRef.current = true
    startVoice()
    // `startVoice` changes identity on every transcript chunk; the ref is what gates this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoStartNow])

  const handleSetMode = useCallback(
    (next: AriaInteractionMode) => {
      // Whether a session — of either kind — is running right now. Read before `setMode`, because the
      // modality that session was minted with is the one it keeps until it is closed.
      const wasSessionLive = isVoiceActiveRef.current
      const wasSpoken = isSpokenModeRef.current
      setMode(next)

      if (!isSpokenAriaMode(next)) {
        // Already typed: a live typed session simply carries on, in the same thread.
        if (!wasSessionLive || !wasSpoken) return
        // Typing continues the same thread. Order matters: stop releases the queued captions, the echo
        // shows them, and only then does the save consume them.
        stopListening()
        chat.absorbSpokenTurns(takeUnsavedTurns(true).turns)
        void savePendingTurns()
        return
      }

      // Choosing a spoken mode is itself the gesture, so this may prompt for the microphone.
      hasAutoStartedRef.current = true
      if (!wasSessionLive) {
        startVoice()
        return
      }
      // A session is open on the other modality and has to be closed and saved first: the hook refuses
      // a second start, so opening on top would leave a typed session running behind a microphone UI.
      if (wasSpoken) return
      stopListening()
      void savePendingTurns().then(startVoice)
    },
    [chat, savePendingTurns, setMode, startVoice, stopListening, takeUnsavedTurns]
  )

  // The spoken language is locked into the ephemeral token, so a live session has to be re-minted.
  // It pauses rather than stops, so the conversation and its transcript carry across the switch.
  const restartVoiceForLanguage = useCallback(() => {
    if (!isVoiceActive) return
    void pauseVoice().then(startVoice)
  }, [isVoiceActive, pauseVoice, startVoice])

  return {
    chat,
    session,
    mode,
    isVoiceActive,
    hasJustCompletedGoals,
    isFirstGoalSetting,
    setMode: handleSetMode,
    openConversation,
    leaveForFocus,
    sendTypedMessage: () => void sendTypedMessage(),
    startVoice,
    stopVoice,
    restartVoiceForLanguage
  }
}
